import {
  defaultLang,
  type SupportedLangs,
  supportedLangs,
} from '@geartracker/types';

import type { ResourceKey } from 'i18next';

// ----------------------------------------------------------------------

/**
 * One dynamic import per language. Vite emits each JSON as its own chunk, so a
 * session downloads only the ~135 KB it renders instead of all five bundles
 * (~655 KB) in the entry chunk.
 *
 * Written as an explicit map rather than a computed `import(./langs/${lng})`:
 * the emitted set stays closed to the five known locales, and a typo is a
 * compile error instead of a 404 at runtime.
 */
const loaders: Record<SupportedLangs, () => Promise<{ default: ResourceKey }>> =
  {
    en: () => import('./langs/en.json'),
    fr: () => import('./langs/fr.json'),
    es: () => import('./langs/es.json'),
    it: () => import('./langs/it.json'),
    de: () => import('./langs/de.json'),
  };

function isSupportedLang(lng: string): lng is SupportedLangs {
  return supportedLangs.some((supported) => supported === lng);
}

/**
 * Clamps any detected language tag to one we actually ship a bundle for:
 * region-qualified tags narrow to their base language (`fr-FR` → `fr`) and
 * anything unsupported falls back to the default.
 *
 * The app relies on this to always resolve a shipped locale, which is what lets
 * i18next run with `fallbackLng: false` and load a single bundle.
 */
export function resolveSupportedLang(
  lng: string | undefined | null,
): SupportedLangs {
  const base = (lng ?? '').slice(0, 2).toLowerCase();

  return isSupportedLang(base) ? base : defaultLang;
}

/**
 * Fetches one language bundle. Strict on purpose: callers resolve the tag with
 * `resolveSupportedLang` first, so an unsupported language here means a bug
 * upstream and should surface rather than silently serve English.
 */
export async function loadTranslations(lng: string): Promise<ResourceKey> {
  if (!isSupportedLang(lng)) {
    throw new Error(`Unsupported language: ${lng}`);
  }

  const { default: bundle } = await loaders[lng]();

  return bundle;
}
